import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ComplianceButton } from '@/components/lumen/ComplianceButton';
import { NAV_GROUPS, NAV_LABEL, siteScopeOf, plantScopeOf } from '@/app/nav';
import { ViewErrorBoundary } from '@/app/ErrorBoundary';
import { LumenSetPassword, LumenSignIn, type SignInEntry } from '@/modules/auth/signin/LumenSignIn';
import { readAuthLink } from '@/modules/auth/auth-fragment';
import { SilenceRevealProvider } from '@/lib/SilenceRevealProvider';
import { AuthProvider } from '@/lib/auth';
import { useAuth } from '@/lib/auth-context';
import { useDeviceCounts } from '@/lib/api/onboarding';
import { useFindingsSummary, useSite } from '@/lib/api/sites';
import { useMe } from '@/lib/api/me';
import { useWriteRequests } from '@/lib/api/writes';
import { SiteProvider } from './site-context';
import { PlantProvider } from './plant-context';
import { OrgProvider } from './org-context';
import { useSiteContext } from './site-context-value';
import { showsFirstSite } from './first-site';
import { FirstSiteSteps } from './FirstSiteSteps';
import { HeaderScope } from '@/components/lumen/HeaderScope';
import { FindingsButton } from '@/components/lumen/FindingsButton';
import { AccountMenu } from '@/components/lumen/AccountMenu';
import { AlarmBell, AlarmToasts } from '@/components/lumen/AlarmToasts';
import { Toasts } from '@/components/lumen/Toasts';
import { useAlarmFeed } from './alarm-feed';
import { useAlarmSummary } from '@/lib/api/queries';
import { useAlarmStream } from '@/lib/api/events';
import { useQueryClient } from '@tanstack/react-query';
import { NOTIFICATIONS_KEY, useNotifications } from '@/lib/api/notifications';
import { freshNotices } from '@/lib/notice-popups';
import { api } from '@/lib/api/client';
import { toast } from './toasts';
import { useWorkOrderFocus } from '@/modules/workorders/focus';
import { Providers } from './providers';
import { Page, Shell } from './Shell';
import { BrandMark } from '@/components/lumen/BrandMark';
import { Icon } from '@/components/lumen/Icon';
import { StatusBadge } from '@/components/lumen/StatusBadge';

/*
 * EVERY MODULE VIEW IS A SEPARATE CHUNK.
 *
 * The whole console used to ship as one 4.30 MB file (1.26 MB gzipped), so an operator
 * downloaded Mapbox GL and Highcharts — the two heaviest things here by far — before the
 * SIGN-IN screen could paint, on every cold load, whether or not they ever opened a map
 * or a chart. Splitting at the view boundary rather than at each library's call site is
 * what makes that automatic: Mapbox follows the views that draw a map, Highcharts follows
 * the views that draw a chart, and neither follows anyone else.
 *
 * The sign-in screen and the shell are deliberately NOT lazy: they are the first paint,
 * and a chunk boundary there would trade the problem for a blank frame.
 *
 * `.then(m => ({ default: m.X }))` because these are named exports and React.lazy resolves
 * a module's `default`.
 */
const ComplianceStage = lazy(() => import('@/modules/compliance/ComplianceStage').then((m) => ({ default: m.ComplianceStage })));
const OverviewView = lazy(() => import('@/modules/overview/OverviewView').then((m) => ({ default: m.OverviewView })));
const IntegrationsView = lazy(() => import('@/modules/manage/IntegrationsView').then((m) => ({ default: m.IntegrationsView })));
const DeviceManagementView = lazy(() => import('@/modules/manage/DeviceManagementView').then((m) => ({ default: m.DeviceManagementView })));
const FindingsView = lazy(() => import('@/modules/findings/FindingsView').then((m) => ({ default: m.FindingsView })));
const SiteOnboardingView = lazy(() => import('@/modules/onboarding/SiteOnboardingView').then((m) => ({ default: m.SiteOnboardingView })));
const SiteHubView = lazy(() => import('@/modules/sites/SiteHubView').then((m) => ({ default: m.SiteHubView })));
const SitesView = lazy(() => import('@/modules/sites/SitesView').then((m) => ({ default: m.SitesView })));
const StandardsView = lazy(() => import('@/modules/standards/StandardsView').then((m) => ({ default: m.StandardsView })));
const CalculatorsView = lazy(() => import('@/modules/calculators/CalculatorsView').then((m) => ({ default: m.CalculatorsView })));
const SldStudioView = lazy(() => import('@/modules/topology/SldStudioView').then((m) => ({ default: m.SldStudioView })));
const TelemetryView = lazy(() => import('@/modules/telemetry/TelemetryView').then((m) => ({ default: m.TelemetryView })));
const DigitalTwinView = lazy(() => import('@/modules/twin/DigitalTwinView').then((m) => ({ default: m.DigitalTwinView })));
const AlarmsView = lazy(() => import('@/modules/alarms/AlarmsView').then((m) => ({ default: m.AlarmsView })));
const WorkOrdersView = lazy(() => import('@/modules/workorders/WorkOrdersView').then((m) => ({ default: m.WorkOrdersView })));
const AlarmDetailPage = lazy(() =>
  import('@/modules/alarms/AlarmDetailPage').then((m) => ({ default: m.AlarmDetailPage })),
);
const ReportsView = lazy(() => import('@/modules/reports/ReportsView').then((m) => ({ default: m.ReportsView })));
const UsersView = lazy(() => import('@/modules/admin/UsersView').then((m) => ({ default: m.UsersView })));
const AccessView = lazy(() => import('@/modules/admin/AccessView').then((m) => ({ default: m.AccessView })));
const PerformanceView = lazy(() =>
  import('@/modules/performance/PerformanceView').then((m) => ({ default: m.PerformanceView })),
);
const PersonPage = lazy(() => import('@/modules/performance/PersonPage').then((m) => ({ default: m.PersonPage })));
const AuditView = lazy(() => import('@/modules/admin/AuditView').then((m) => ({ default: m.AuditView })));
const OrganisationView = lazy(() =>
  import('@/modules/admin/OrganisationView').then((m) => ({ default: m.OrganisationView })),
);
const ProfileView = lazy(() => import('@/modules/admin/ProfileView').then((m) => ({ default: m.ProfileView })));
const RemoteControlView = lazy(() => import('@/modules/writes/RemoteControlView').then((m) => ({ default: m.RemoteControlView })));

/**
 * Application root.
 *
 * The auth gate is OUTSIDE the shell on purpose. Rendering the sidebar and then filling
 * every panel with an error looks like the product is broken rather than that nobody is
 * signed in.
 *
 * Navigation is still `tab` state rather than TanStack Router. Known gap: it costs
 * shareable URLs — a link to one site's device queue, or to a filtered findings view —
 * which is what ADR-0005's search-param schemas are for. Deliberately not bundled with
 * the module work, because converting the shell touches every screen at once.
 *
 * One consequence of that gap is load-bearing here: because the shell owns the whole URL,
 * a route that has to work BEFORE sign-in cannot be a tab. `PUBLIC_ROUTES` below is that
 * second, pre-auth list, and `/accept-invite` is its first member.
 */
export function App(): ReactNode {
  return (
    <Providers>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </Providers>
  );
}

/**
 * THE AUTH GATE, WHICH NOW ALSO ROUTES AN INVITATION
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BELT AND BRACES: TWO INDEPENDENT REASONS TO DEMAND A PASSWORD
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `mustChangePassword` reads `app_metadata.must_change_password`, which the API sets when
 * it issues the invite. That is the primary mechanism and it is the right one — it survives
 * a reload, a different browser, and a user who bookmarks the console.
 *
 * It is not sufficient on its own. It is a WRITE, on the server, at invite time; if it
 * failed or was skipped, an invited user arriving on a link would be dropped straight onto
 * the console having never chosen a password — a credential that is still the one from the
 * email, and therefore still shared, which is precisely what makes an audit trail
 * unattributable. So `type=invite` on the link is honoured as well, independently.
 *
 * Held in state rather than read during render: the intent has to be forgettable. Once the
 * password is set, `LumenSetPassword` reports back and the gate lets go — a module-level
 * read would hold the operator on that screen until they reloaded.
 */
function Gate(): ReactNode {
  const { session, loading, mustChangePassword } = useAuth();
  const [linkIntent, setLinkIntent] = useState<'invite' | 'recovery' | null>(linkIntentOnLoad);
  /*
   * AN UNREDEEMED LINK HOLDS THE SIGN-IN FRAME UP, EVEN OVER A SESSION.
   *
   * The link waits for a press (`ConfirmScreen.tsx`), so until it is settled the frame must
   * stay — including for someone already signed in on this browser, who would otherwise be
   * dropped into the console with the link they just opened silently ignored. A link that
   * redeemed straight into the fragment always replaced the session; this keeps that meaning.
   * Settled — redeemed, refused, or set aside with "Back to sign in" — it lets go, and the
   * frame's own state carries on: same element, same place in the tree.
   */
  const [confirming, setConfirming] = useState<boolean>(
    () => readAuthLink()?.kind === 'confirm',
  );

  /*
   * THE LINK'S INTENT DIES WITH THE SESSION IT ARRIVED ON.
   *
   * Without this, someone who followed a recovery link, decided not to change their password
   * and signed out was trapped: `linkIntent` outlived the session, so signing back in with
   * their existing, perfectly good password put them straight back on the set-password
   * screen — `mustChangePassword` false, `linkIntent` still 'recovery' — and the only ways
   * out were to set a password or to loop through sign-out again.
   *
   * An intent is a fact about ONE arrival. When the session it came with ends, so does it.
   */
  useEffect(() => {
    if (session === null) setLinkIntent(null);
  }, [session]);

  // Hold the frame until the persisted session resolves; otherwise a reload flashes the
  // login page for anyone already signed in.
  if (loading) return <Splash />;
  if (!session || confirming) {
    /*
     * No session, but possibly a reason to be here anyway: the invitation landing route, or
     * a link that GoTrue refused. Both need the code entry rather than a password form —
     * the person has no password yet, so the password form is the one screen that cannot
     * help them.
     */
    const entry = signInEntry(confirming);
    return (
      <LumenSignIn
        {...(entry ? { entry } : {})}
        onIntent={setLinkIntent}
        onLinkSettled={() => setConfirming(false)}
      />
    );
  }
  if (mustChangePassword || linkIntent) {
    /* `reset` only when the LINK said recovery. A `must_change_password` flag means the
       credential was issued, which is the stronger claim and the one to print. */
    return (
      <LumenSetPassword
        onDone={() => setLinkIntent(null)}
        reason={!mustChangePassword && linkIntent === 'recovery' ? 'reset' : 'issued'}
      />
    );
  }
  return (
    /* OUTERMOST OF THE THREE SCOPES. Organisation → Site → Plant, and each resets the ones
       inside it — a site id means nothing under a different organisation, exactly as a plant id
       means nothing under a different site. It also decides where WRITES go, which is why it
       wraps everything rather than sitting beside the others. */
    <OrgProvider>
      <SiteProvider>
        {/* Inside the site provider, because a plant is a part of the SELECTED site and the
            selection resets whenever that site changes. Renders nothing for a site whose
            topology does not divide, which is both live sites today. */}
        <PlantProvider>
          <AppInner />
        </PlantProvider>
      </SiteProvider>
    </OrgProvider>
  );
}

/** `magiclink` is deliberately absent: it signs you in, it does not oblige a new password. */
function linkIntentOnLoad(): 'invite' | 'recovery' | null {
  const link = readAuthLink();
  if (link?.kind !== 'session') return null;
  return link.type === 'invite' || link.type === 'recovery' ? link.type : null;
}

/**
 * Why the sign-in frame should open on the code screen rather than the password form.
 *
 * Two ways in, and the second is the one that matters: a link that FAILED. An expired or
 * already-spent invitation has to say so and offer the code from the same email, because
 * the alternative — the ordinary sign-in form — asks for a password the person does not
 * have and never had.
 *
 * The PATH decides which email the person is holding when the fragment cannot. GoTrue's
 * error redirect carries `error_code` and no `type`, so for a failed link the landing route
 * is the only evidence left: invitations arrive at `/accept-invite`, resets at `/`.
 */
function signInEntry(confirming: boolean): SignInEntry | null {
  const invited = publicRouteFromLocation() === 'accept-invite';
  const link = readAuthLink();

  /*
   * An unredeemed link opens the Continue step — once. The capture is module-level and
   * outlives the step, so after it is settled the frame must not offer the same, now spent,
   * token again the next time it mounts (after a sign-out, say).
   */
  if (link?.kind === 'confirm') {
    return confirming ? { purpose: link.type, confirm: link.tokenHash } : null;
  }

  if (link?.kind === 'failure') {
    /*
     * ONLY THREE TYPES HAVE A CODE SCREEN, and the other three used to be answered as if
     * they were a password reset.
     *
     * `parseAuthLink` also returns `signup`, `email_change` and `email`. A failed link of
     * any of those fell through to the `invited ? 'invite' : 'recovery'` default, so a
     * broken email-change confirmation told the reader to "Enter your reset code" — for a
     * reset they never requested, from an email that contains no such code.
     *
     * Those three are mapped to `null` here, and a null purpose means the code screen has
     * nothing to offer: `signInEntry` returns the failure NOTICE without a purpose, so the
     * reader is told the link did not work on the ordinary sign-in screen rather than being
     * sent to a form that cannot help them. None of the three is reachable in the shipped
     * product today (see the email templates' README), which is exactly why a wrong answer
     * here would have gone unnoticed.
     */
    const stated =
      link.type === 'invite' || link.type === 'recovery' || link.type === 'magiclink'
        ? link.type
        : null;
    if (link.type === 'signup' || link.type === 'email_change' || link.type === 'email') {
      /* No purpose: the sign-in screen shows the failure and stays put, because there is no
         code screen that could help and pretending otherwise is what produced "Enter your
         reset code" for a broken email-change confirmation. */
      return { purpose: null, notice: link.message };
    }
    /*
     * `otp_expired` and `access_denied` mean the CREDENTIAL is spent, not just the link —
     * GoTrue issues one per email and the six-digit code is that same credential. A
     * malformed or truncated link is different: the code in the email is still good, which
     * is the case the code screen was written for.
     */
    const expired = link.code === 'otp_expired' || link.code === 'access_denied';
    return {
      purpose: stated ?? (invited ? 'invite' : 'recovery'),
      notice: link.message,
      expired,
    };
  }

  return invited ? { purpose: 'invite' } : null;
}

function Splash(): ReactNode {
  return (
    <div
      className="flex items-center justify-center gap-[10px]"
      /* dvh, not min-h-screen: see the note in Shell.tsx on why 100vh overflows. */
      style={{
        minHeight: '100vh',
        height: '100dvh',
        background: 'var(--bg)',
        color: 'var(--muted-fg)',
      }}
    >
      <BrandMark size={26} />
      <span className="text-[13px]">Loading…</span>
    </div>
  );
}

type Tab =
  | 'overview' | 'twin' | 'findings' | 'telemetry' | 'alarms' | 'work-orders'
  | 'sites' | 'onboarding' | 'integrations' | 'devices' | 'topology' | 'calculators'
  /** Remote control (ADR-0014). Routable only; the rail hides it without the capability. */
  | 'writes'
  /* `metrics` is a SUBPAGE of Device management now, but it stays routable: it was a rail
     entry, so bookmarks and the Findings deep-links point at it. */
  | 'metrics'
  | 'standards' | 'compliance' | 'reports'
  | 'users' | 'performance' | 'access' | 'audit' | 'organisation' | 'profile'
  /** Launched from Sites rather than a nav destination. */
  | 'site-detail'
  /** One alarm's own page, `/alarm?id=…`. Launched from the board, a toast or a link. */
  | 'alarm'
  /** One person's Performance page, `/performance/person?id=…` (2026-10-12). Its URL is not its
      id — see `PERSON_PATH`. */
  | 'performance-person';

/**
 * Every tab id, as a runtime value. `Tab` is a type and types do not survive to runtime, so a
 * URL segment has to be validated against this. Kept adjacent to the union above: adding a tab
 * without adding it here means that tab is reachable by clicking and NOT by URL, which is the
 * kind of asymmetry nobody notices until they bookmark it.
 */
const TABS: readonly Tab[] = [
  'overview', 'twin', 'findings', 'telemetry', 'alarms', 'work-orders',
  'sites', 'onboarding', 'integrations', 'devices', 'metrics', 'topology', 'calculators',
  'writes',
  'standards', 'compliance', 'reports',
  'users', 'performance', 'access', 'audit', 'organisation', 'profile',
  'site-detail',
  'alarm',
  'performance-person',
];

/** The person page sits UNDER Performance in the address, so its path is not its tab id. */
const PERSON_PATH = 'performance/person';

const DEFAULT_TAB: Tab = 'overview';

/** The tab named by the current URL, or the default when the URL names nothing valid. */
function tabFromLocation(): Tab {
  if (typeof window === 'undefined') return DEFAULT_TAB;
  const seg = window.location.pathname.replace(/^\/+|\/+$/g, '');
  if (seg === PERSON_PATH) return 'performance-person';
  return (TABS as readonly string[]).includes(seg) ? (seg as Tab) : DEFAULT_TAB;
}

/**
 * Routes that must work with NO SESSION, listed as a runtime value for the same reason
 * `TABS` is: a type does not survive to runtime, so a URL segment has to be validated
 * against something.
 *
 * The asymmetry the `TABS` note warns about is worse here, not milder. A tab left out of
 * `TABS` is still reachable by clicking; a route left out of THIS list is reachable by
 * nothing at all, because arriving by URL is the only way anyone ever arrives — it is the
 * address printed in an email. `/accept-invite` is where the API now points invitations, so
 * it is here, and it must stay outside `TABS`: a segment in both would be claimed by the
 * shell and rendered as a `Stub` inside a console the invited user cannot yet reach.
 */
const PUBLIC_ROUTES = ['accept-invite'] as const;
type PublicRoute = (typeof PUBLIC_ROUTES)[number];

function publicRouteFromLocation(): PublicRoute | null {
  if (typeof window === 'undefined') return null;
  const seg = window.location.pathname.replace(/^\/+|\/+$/g, '');
  return (PUBLIC_ROUTES as readonly string[]).includes(seg) ? (seg as PublicRoute) : null;
}

function AppInner(): ReactNode {
  /*
   * THE TAB LIVES IN THE URL, and this is a bug fix rather than a feature.
   *
   * It was `useState<Tab>('overview')` with no URL sync of any kind, so the address bar never
   * changed and every REFRESH threw the operator back to Overview — from the middle of a
   * telemetry selection, a half-drawn diagram, or a findings queue they were working through.
   * Browser back and forward did nothing at all for the same reason, and no view in the product
   * could be linked to or bookmarked.
   *
   * `history.pushState` rather than a router: this app has one level of navigation and a
   * fifteen-value union, so a router would be more moving parts than the problem has. The
   * `popstate` listener is what makes the browser's own back/forward work, and reading the URL
   * in the `useState` initialiser is what makes a refresh land where the operator was.
   */
  const [tab, setTab] = useState<Tab>(tabFromLocation);

  /**
   * WHICH ROWS THE AUDIT TRAIL SHOULD OPEN ON.
   *
   * The three Administer screens answer one question between them — who has access, what
   * happened to it, and what is mine — and they could not reach each other. A demotion is a
   * decision about behaviour, and the evidence for it sat one tab away with no way to get
   * from a person to their rows: the reader had to switch tabs and re-pick the same name out
   * of a dropdown. Held here, beside `tab`, for the same reason `selectedId` is: it is
   * shell-level navigation state, not something a page owns.
   *
   * Consumed ONCE. The trail clears it after applying it, so pressing Audit again later
   * shows the whole trail rather than silently re-applying a filter from ten minutes ago.
   */
  const [auditFocus, setAuditFocus] = useState<
    { actorId?: string; entityType?: string; entityId?: string } | null
  >(null);

  const openAuditFor = (filter: { actorId?: string; entityType?: string; entityId?: string }) => {
    setAuditFocus(filter);
    setTab('audit');
  };

  /*
   * The same pattern one axis over: a breakdown row on the Overview opens the Alarms board
   * on the device it names. Held here beside `tab` for the reason `auditFocus` is — it is
   * shell-level navigation state, not something either page owns — and consumed ONCE, so
   * returning to Alarms later shows the whole board rather than silently re-applying a
   * filter from ten minutes ago.
   */
  const [alarmFocus, setAlarmFocus] = useState<{ deviceId?: number } | null>(null);

  /*
   * WHICH ALARM `/alarm` SHOWS. The one tab whose URL carries a parameter, because an alarm page
   * exists to be linked to — from a ticket, a toast, a message — and a link that lands on "an
   * alarm" is no link at all. Read from `?id=` on first paint and on back/forward; written with
   * the tab by the effect below.
   */
  const [alarmId, setAlarmId] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('id'),
  );
  const openAlarm = (id: string) => {
    setAlarmId(id);
    setTab('alarm');
  };

  /* WHICH PERSON `/performance/person` SHOWS — the alarm page's pattern: `?id=` read on first
     paint and on back/forward, written with the tab by the effect below. */
  const [personId, setPersonId] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : new URLSearchParams(window.location.search).get('id'),
  );
  const openPerson = (id: string) => {
    setPersonId(id);
    setTab('performance-person');
  };

  /*
   * Compliance is site-scoped and reads the header's selection, so the site is chosen HERE
   * before the tab changes — a navigation that skipped it would open the matrix on whatever
   * site was already selected and show the wrong plant's verdicts under the right heading.
   * The Exposure cell selects too; doing it in both places is deliberate belt and braces,
   * and `select` is idempotent.
   */
  const openComplianceFor = (siteId: string) => {
    selectSite(siteId);
    setTab('compliance');
  };

  const openAlarmsFor = (focus: { deviceId?: number }) => {
    setAlarmFocus(Object.keys(focus).length > 0 ? focus : null);
    setTab('alarms');
  };

  /* Push on change, so back/forward walk the history the operator actually made. Replace on
   * first paint instead, so landing on `/` does not leave an empty entry behind them. */
  useEffect(() => {
    const seg =
      tab === 'alarm' && alarmId !== null
        ? `/alarm?id=${encodeURIComponent(alarmId)}`
        : tab === 'performance-person'
          ? `/${PERSON_PATH}${personId !== null ? `?id=${encodeURIComponent(personId)}` : ''}`
          : `/${tab}`;
    if (`${window.location.pathname}${window.location.search}` === seg) return;
    /* Already on this page: its query is the PAGE's to own (`SiteHubView` keeps `?tab=&view=`).
       Rewriting to the bare path here wiped `/site-detail?tab=analytics` on a reload before the
       hub could read it, and every shared link to a site sub-tab opened Diagrams instead. */
    if (tab !== 'alarm' && tab !== 'performance-person' && window.location.pathname === seg) return;
    const from = window.location.pathname.replace(/^\/+|\/+$/g, '');
    /*
     * A public route is REPLACED, not pushed past. An invitation URL is single-use: leaving
     * it in the history gives the operator a Back button that returns to a page asking them
     * to accept an invitation they have already accepted.
     */
    const entry = from === '' || (PUBLIC_ROUTES as readonly string[]).includes(from);
    window.history[entry ? 'replaceState' : 'pushState']({ tab }, '', seg);
  }, [tab, alarmId, personId]);

  /* The browser's own navigation. Without this, back/forward change the URL and not the view —
   * which is worse than not supporting them, because the address bar then lies. */
  useEffect(() => {
    const onPop = (): void => {
      setTab(tabFromLocation());
      setAlarmId(new URLSearchParams(window.location.search).get('id'));
      setPersonId(new URLSearchParams(window.location.search).get('id'));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const counts = useDeviceCounts();
  const {
    selectedId,
    selected,
    select: selectSite,
    sites: allSites,
    loading: sitesLoading,
    error: sitesError,
  } = useSiteContext();
  /*
   * ── AN ORGANISATION WITH NO SITE SEES THE WAY IN, NOT ZEROS (2026-09-30) ─────────
   * Every site-scoped screen shows `FirstSiteSteps` while the site list has answered empty — see
   * `first-site.ts` for the rule. `integrationsSub` lets step 3 land on Discovered devices rather
   * than on the gateways page the person has just come from.
   */
  const firstSite = showsFirstSite({
    scope: siteScopeOf(tab),
    loading: sitesLoading,
    error: sitesError,
    siteCount: allSites.length,
  });
  const noSites = !sitesLoading && !sitesError && allSites.length === 0;
  const [integrationsSub, setIntegrationsSub] = useState<'gateways' | 'devices'>('gateways');
  /*
   * Read early, so the query below can gate itself on it. `held`/`hiddenItems` further down
   * still read `me.data` too — calling the hook once and reading it twice costs nothing extra,
   * TanStack Query dedupes by key.
   */
  const me = useMe();
  const canReadFindings = me.data?.permissions.includes('findings.read') ?? false;
  const canCreateSite = me.data?.permissions.includes('sites.create') ?? false;
  /*
   * UNRESOLVED FINDINGS, FOR THE RAIL'S BADGE — scoped to the selected site (Hamzah,
   * 2026-09-09), like the alarm badge below it and unlike before. The badge counted the fleet
   * while the Findings page it opens on defaults to the site, and a "7" that opens a list of
   * two teaches the operator that the rail's numbers are not to be trusted.
   *
   * ENABLED ONLY WITH `findings.read` (0083). Other screens — this rail badge among them —
   * must quietly omit a findings figure without the permission, never show an error state, and
   * the FIRST way to do that is to never issue the request the API would now 403. Fails closed
   * while `me.data` is in flight, same as `hiddenItems` below.
   */
  const findings = useFindingsSummary(selectedId ?? undefined, canReadFindings);
  /*
   * OPEN ALARMS, FOR THE RAIL'S OWN BADGE.
   *
   * Scoped to the selected site exactly as every other alarm surface is, so the rail agrees
   * with the Alarms view an operator reaches by clicking it. It deliberately does NOT reuse
   * the bell's number: the bell counts alarm EVENTS this browser has not looked at yet and
   * resets to zero when opened, which is a property of this tab. `open` is how many alarms
   * are currently raised at the plant — a property of the plant, true on every screen, and
   * the only one of the two that means anything in a navigation rail.
   */
  const alarmSummary = useAlarmSummary(selectedId ?? undefined);
  const openAlarms = alarmSummary.data?.open ?? 0;
  const alarmsCritical = (alarmSummary.data?.bySeverity.critical ?? 0) > 0;

  /*
   * The remote-control rail entry exists only for someone who can act on it. Both flags
   * arrive from /me already AND-ed with the organisation's `remote_control_enabled` gate,
   * so an org with the feature off simply has no entry — the honest default for a
   * capability whose default is OFF. `me` itself is read further up, beside `findings`.
   */
  const canProposeWrites = me.data?.can.proposeWrites ?? false;
  const canApproveWrites = me.data?.can.approveWrites ?? false;

  /*
   * WHAT THIS PERSON IS NOT OFFERED, and it was one id long.
   *
   * Until today this read `canProposeWrites || canApproveWrites ? [] : ['writes']` — a single
   * capability-gated destination — and nothing else in the rail consulted a permission at all.
   * Signing in as each of the four real roles returned the SAME 23 entries every time: an
   * Operator holding `alarms.triage` and `users.read` was offered Access, Audit trail, SLD
   * Studio, Onboarding and Device management, and every one of them answered 403.
   *
   * TWO SOURCES, JOINED HERE RATHER THAN MERGED, because they mean different things:
   *
   *   · `item.needs` is a PERMISSION. Absent means anybody signed in may look, which is most
   *     screens — see the long note on `NavItem.needs` for why reading the fleet, the alarm
   *     board or a sealed report is not gated.
   *   · `writes` is a permission AND an ORGANISATION gate (ADR-0014). Its entry stays on the
   *     line below so the distinction survives: "your role cannot" and "this organisation has
   *     it switched off" are different sentences, and `can.proposeWrites` has already AND-ed
   *     them, which a slug lookup here could not express.
   *
   * FAILS CLOSED WHILE `/me` IS IN FLIGHT. `me.data` is undefined on the first render, so the
   * set is empty and everything gated is hidden until the answer arrives. The alternative —
   * showing everything and removing entries a moment later — is a rail that flickers items an
   * operator may have already reached for.
   */
  const held = useMemo(() => new Set(me.data?.permissions ?? []), [me.data?.permissions]);
  const hiddenItems = useMemo(
    () => [
      ...NAV_GROUPS.flatMap((g) =>
        g.items.filter((i) => i.needs !== undefined && !held.has(i.needs)).map((i) => i.id),
      ),
      ...(canProposeWrites || canApproveWrites ? [] : ['writes']),
    ],
    [held, canProposeWrites, canApproveWrites],
  );

  /*
   * AND A SCREEN THAT IS NO LONGER OFFERED MUST NOT STILL BE SHOWING.
   *
   * Hiding the rail entry is only half of it. `tab` is state, restored from the URL at
   * `tabFromLocation()`, so three routes reach a hidden screen with the entry already gone:
   * a bookmarked or shared link, a browser Back into one, and an administrator changing
   * somebody's role while they have it open — role is resolved PER REQUEST, so the next
   * `/me` answers with the new set and the rail loses the entry under them.
   *
   * In all three the view keeps rendering and every query inside it 403s, which reads as a
   * broken screen rather than as access they do not have. Falling back to `overview` is the
   * one destination no permission gates.
   *
   * Waits for `/me` to answer. Until then `hiddenItems` is everything gated — see the note
   * above — and acting on that would bounce a legitimate deep link off its own screen.
   */
  useEffect(() => {
    if (!me.data) return;
    /* A person's page is Performance's, and is gone with it. */
    if (hiddenItems.includes(tab) || (tab === 'performance-person' && hiddenItems.includes('performance'))) setTab('overview');
  }, [me.data, hiddenItems, tab]);
  /*
   * The pending count is a QUEUE for approvers — each one is a colleague waiting on a
   * second signature against a 24 h clock — so it is badged like the device queue is.
   * Fetched only when this user can actually approve: for anyone else the number is not
   * actionable, and the request would be capability-refused anyway.
   */
  const writeQueue = useWriteRequests({ status: 'pending', limit: 1 }, canApproveWrites);
  const pendingWrites = canApproveWrites ? (writeQueue.data?.pending ?? 0) : 0;

  // Compliance evaluates the SELECTED site now, not an archetype template. The template
  // switcher that stood in for this scored a site nobody owns.
  const site = useSite(tab === 'compliance' ? selectedId : null);

  const pending = counts.data?.counts.pending ?? 0;

  /*
   * THE LIVE ALARM STREAM, opened once for the whole app.
   *
   * Here rather than inside `AlarmBell`, because the connection is per PROCESS and not per
   * component: two components calling `useAlarmStream` would open two streams and double
   * every toast. The bell and the toast host both read the store this fills.
   *
   * `enabled: true` unconditionally, because this component only renders behind the auth
   * gate — and the hook re-checks the session on every connect anyway, reporting `offline`
   * rather than retrying into a 401 loop if it has gone.
   */
  const pushAlarm = useAlarmFeed((s) => s.push);
  const setStream = useAlarmFeed((s) => s.setStream);
  const qc = useQueryClient();

  /* A work order from a notification: the Work orders screen opens that order
     (`modules/workorders/focus.ts`). It was Alarms → Work until 2026-10-12. */
  const openWorkOrder = (id: string) => {
    useWorkOrderFocus.getState().open(id);
    setTab('work-orders');
  };

  /* A work order from an EMAIL: `/work-orders?workOrder=<id>` (`notice-mail.ts::entityUrl`) — and
     `/alarms?workOrder=<id>` from a mail sent before the screen moved, which is why the parameter is
     read on any path. There is no router, so the one deep link a mail needs is read once at load,
     the same way `/alarm?id=` is above, and handed to the same focus store a notification uses. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const id = new URLSearchParams(window.location.search).get('workOrder');
    if (id) openWorkOrder(id);
    // Once, at load: a later navigation is the app's own, not the mail's.
  }, []);

  /* `notice` frames invalidate the inbox inside `useAlarmStream`; what pops up is decided below,
     from the inbox, so a live frame, a reconnect and the offline poll all take one path. */
  const streamState = useAlarmStream({ onAlarm: pushAlarm, enabled: true });

  /*
   * POP-UPS FOR THIS PERSON'S NOTIFICATIONS (`lib/notice-popups.ts`).
   *
   * Polled every 30 s while the stream is not live — the bell's red dot — and every 2 min as a
   * backstop while it is, so a notice sent during a disconnect pops up within half a minute
   * rather than waiting for the reconnect. "Open" marks it read and goes to the order or alarm.
   */
  const inbox = useNotifications({ pollMs: streamState === 'live' ? 120_000 : 30_000 });
  const popupLine = useRef<string | null>(null);
  useEffect(() => {
    const rows = inbox.data?.data;
    if (rows === undefined) return;
    const { popups, line } = freshNotices(rows, popupLine.current);
    popupLine.current = line;
    for (const n of popups) {
      toast.info(
        n.title,
        n.body ?? undefined,
        /* A work order opens its panel; an alarm given to you (since 2026-10-05) opens its page. */
        n.entityType === 'work_order' || n.entityType === 'alarm'
          ? {
              label: 'Open',
              run: () => {
                void api
                  .post('/me/notifications/read', { ids: [n.id] })
                  .then(() => qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }));
                if (n.entityType === 'alarm') openAlarm(n.entityId);
                else openWorkOrder(n.entityId);
              },
            }
          : undefined,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `openWorkOrder` is a fresh closure per render; the inbox data is the only trigger
  }, [inbox.data]);
  useEffect(() => {
    setStream(streamState);
  }, [streamState, setStream]);

  /** Sub-views of Sites keep the rail on Sites. */
  /* `metrics` is a subpage of Device management, so the rail must highlight its parent —
     otherwise a bookmarked /metrics shows no active entry at all. */
  const railTab =
    tab === 'site-detail'
      ? 'sites'
      : tab === 'metrics'
        ? 'devices'
        : tab === 'alarm'
          ? 'alarms'
          : tab === 'performance-person'
            ? 'performance'
            : tab;

  return (
    <Shell
      tab={railTab}
      onTab={(id) => setTab(id as Tab)}
      findingCount={findings.data?.unresolved ?? 0}
      badges={{
        devices: pending > 0 ? { count: pending, tone: 'warn' } : undefined,
        writes: pendingWrites > 0 ? { count: pendingWrites, tone: 'warn' } : undefined,
        /* Danger only when something critical is raised. A rail where every badge is red
           teaches an operator to ignore red. */
        alarms:
          openAlarms > 0
            ? { count: openAlarms, tone: alarmsCritical ? 'danger' : 'warn' }
            : undefined,
      }}
      hiddenItems={hiddenItems}
      onOpenAlarm={openAlarm}
      headerSlot={
        <>
          {/*
            * ONE BUTTON, ONE POPOVER — not three bordered controls, and not the breadcrumb
            * that replaced them first (rejected: too wide, three carets, everything at equal
            * weight). `HeaderScope` owns organisation, site and plant behind a single control;
            * see that file for the accessibility contract a popover has to rebuild that a
            * native `<select>` gave away for free.
            */}
          <HeaderScope
            scope={siteScopeOf(tab)}
            plantInScope={plantScopeOf(tab)}
            onAddSite={canCreateSite ? () => setTab('onboarding') : undefined}
          />
          {/*
            * THE POSTURE ROW — Alarms · Findings · Compliance, ORDERED BY HOW SOON EACH NEEDS
            * YOU. Alarms are live and can be firing right now; findings are derived gaps that
            * do not change on their own between visits; compliance is the standing score they
            * both feed. The bell used to sit after the compliance chip — it now leads the row,
            * and findings (previously absent from the header entirely, though the rail has
            * carried its count since Findings shipped) sits between the two. The account menu
            * is unaffected: it is rendered last by the Shell itself (`accountSlot`), so
            * nothing added here can displace it from the corner.
            *
            * FINDINGS AND COMPLIANCE ARE ICON BUTTONS WITH A DOT, NOT PILLS, as of this
            * change. The two used to spell their numbers out inline (`⚠ 2 findings`,
            * `● 83% partial | 2 open`) — together with `HeaderScope` and the bell that was
            * four controls' worth of width for what a 32px icon now covers for two of them,
            * ~250px reclaimed. The numbers did not go away; they moved into a popover behind
            * each icon (`FindingsButton`, `ComplianceButton`), and only the alarm bell still
            * carries a number rather than a dot — see `HeaderStatButton`'s file comment for
            * why a count is worth the header's space for alarms and for neither of the other
            * two. Three icons, three different silhouettes (bell / triangle / shield) and
            * only the bell may glow: see the same file for both rules.
            */}
          <AlarmBell onOpenAlarm={openAlarm} onOpenWorkOrder={openWorkOrder} />
          {/*
            * HIDDEN WITHOUT THE PERMISSION (0083), the same set `hiddenItems` reads. Not
            * `hiddenItems` itself — these two are header buttons, not rail entries — but the
            * same rule: a control the API would now refuse must not be drawn.
            */}
          {held.has('findings.read') && (
            <FindingsButton
              summary={findings.data}
              loading={findings.isLoading}
              siteName={selected?.name}
              onOpen={() => setTab('findings')}
            />
          )}
          {held.has('compliance.read') && (
            <ComplianceButton onOpen={() => setTab('compliance')} />
          )}
        </>
      }
      accountSlot={<AccountMenu />}
    >
      {/*
        * The toast host, INSIDE the Shell but outside the error boundary.
        *
        * Outside on purpose: a crashed view must not take the alarm notifications with it.
        * A screen that has thrown is exactly when an operator most needs to be told the
        * plant is doing something, and a toast host inside the boundary would be unmounted
        * by the very failure they need to work around.
        */}
      <AlarmToasts onOpenAlarm={openAlarm} />
      {/* The console's own pop-ups — a notification for this person, with "Open". It was mounted
          on the sign-in screen only, so a toast raised anywhere in the console showed nothing. */}
      <Toasts belowHeader />

      {/*
        * Inside the Shell, around the routed view only: the sidebar and header stay alive
        * so a crashed screen is something to navigate away from rather than a white page.
        * Keyed on `tab` so moving to another module clears the error.
        */}
      <ViewErrorBoundary resetKey={tab} label={NAV_LABEL[tab] ?? tab}>
      {/* "Show last readings" and the day it moves a page to, for this page view: keyed on the tab
          so every other page starts from its not-reporting screen (`lib/use-silence-reveal.tsx`). */}
      <SilenceRevealProvider key={tab}>
      <Suspense fallback={<ViewLoading label={NAV_LABEL[tab] ?? tab} />}>
      {firstSite ? (
        <FirstSiteSteps
          title={NAV_LABEL[tab] ?? 'Overview'}
          siteCount={allSites.length}
          canAddSite={canCreateSite}
          onAddSite={() => setTab('onboarding')}
          onOpenGateways={() => {
            setIntegrationsSub('gateways');
            setTab('integrations');
          }}
          onOpenDevices={() => {
            setIntegrationsSub('devices');
            setTab('integrations');
          }}
        />
      ) : tab === 'overview' ? (
        <OverviewView
          onOpenAlarms={openAlarmsFor}
          onOpenCompliance={openComplianceFor}
          onOpenWorkOrders={() => setTab('work-orders')}
        />
      ) : tab === 'sites' ? (
        <SitesView
          onOnboard={() => setTab('onboarding')}
          onOpenDevices={() => setTab('devices')}
          onOpenSite={() => setTab('site-detail')}
        />
      ) : tab === 'site-detail' && selectedId ? (
        /*
         * The site HUB, not the old single-page detail view.
         *
         * `SiteHubView` is the multi-page site workspace — SLD, Parameters, Alarms, Analytics,
         * Devices, Detail — and it was built but never routed, so clicking a site landed on
         * `SiteDetailView` instead and none of those pages was reachable from anywhere in the
         * product. `SiteDetailView` is now unused; it stays in the tree only until its
         * remaining copy is folded into the hub's Detail tab.
         */
        <SiteHubView
          siteId={selectedId}
          onBack={() => setTab('sites')}
          onOpenDeviceQueue={() => setTab('devices')}
          onOpenFindings={() => setTab('findings')}
          onOpenTelemetry={() => setTab('telemetry')}
        />
      ) : tab === 'onboarding' ? (
        <SiteOnboardingView
          onFinish={() => setTab('sites')}
          onOpenDevices={() => setTab('devices')}
        />
      ) : tab === 'integrations' ? (
        <IntegrationsView key={integrationsSub} initialSub={integrationsSub} noSites={noSites} />
      ) : tab === 'devices' ? (
        <DeviceManagementView />
      ) : tab === 'findings' ? (
        <FindingsView {...(selectedId ? { siteId: selectedId } : {})} />
      ) : tab === 'standards' ? (
        <StandardsView />
      ) : tab === 'calculators' ? (
        <CalculatorsView />
      ) : tab === 'writes' ? (
        <RemoteControlView />
      ) : tab === 'reports' ? (
        <ReportsView />
      ) : tab === 'users' ? (
        <UsersView onOpenAudit={openAuditFor} onOpenProfile={() => setTab('profile')} />
      ) : tab === 'performance' ? (
        <PerformanceView onOpenPerson={openPerson} />
      ) : tab === 'performance-person' ? (
        <PersonPage
          personId={personId}
          onBack={() => setTab('performance')}
          onOpenAlarm={openAlarm}
          onOpenWorkOrder={openWorkOrder}
        />
      ) : tab === 'access' ? (
        <AccessView />
      ) : tab === 'audit' ? (
        <AuditView
          {...(auditFocus ? { focus: auditFocus } : {})}
          onFocusApplied={() => setAuditFocus(null)}
          onOpenUsers={() => setTab('users')}
        />
      ) : tab === 'organisation' ? (
        <OrganisationView
          onOpenUsers={() => setTab('users')}
          onOpenDevices={() => setTab('devices')}
          onOpenAccess={() => setTab('access')}
        />
      ) : tab === 'profile' ? (
        <ProfileView onOpenAudit={openAuditFor} onOpenUsers={() => setTab('users')} />
      ) : tab === 'twin' ? (
        <DigitalTwinView onOpenAlarms={() => setTab('alarms')} />
      ) : tab === 'topology' ? (
        <SldStudioView {...(selectedId ? { siteId: selectedId } : {})} />
      ) : tab === 'metrics' ? (
        /* The old rail entry. Same screen, reached through its container so the sub-nav is
           present — a bookmark should not land somewhere with no way back to its siblings. */
        <DeviceManagementView />
      ) : tab === 'telemetry' ? (
        <TelemetryView onOpenDevices={() => setTab('devices')} />
      ) : tab === 'alarms' ? (
        <AlarmsView
          {...(alarmFocus?.deviceId !== undefined ? { focusDeviceId: alarmFocus.deviceId } : {})}
          onFocusApplied={() => setAlarmFocus(null)}
          onOpenAlarm={openAlarm}
        />
      ) : tab === 'alarm' ? (
        <AlarmDetailPage alarmId={alarmId} onBack={() => setTab('alarms')} onOpenAlarm={openAlarm} />
      ) : tab === 'work-orders' ? (
        <WorkOrdersView onOpenAlarm={openAlarm} />
      ) : tab === 'compliance' ? (
        site.data ? (
          <ComplianceStage
            siteId={site.data.site.id}
            siteName={selected?.name ?? 'Site'}
            params={site.data.site.params}
          />
        ) : (
          <Page title="Compliance" desc="Loading the selected site…">
            <div className="lm-sk h-64 rounded-[var(--radius)]" />
          </Page>
        )
      ) : (
        <Stub tab={tab} />
      )}
      </Suspense>
      </SilenceRevealProvider>
      </ViewErrorBoundary>
    </Shell>
  );
}

/**
 * Nothing is stubbed any more — every entry in NAV_GROUPS routes to a real view.
 *
 * `Stub` is kept as the fallback for an unroutable tab, which is now only reachable via a
 * bad `setTab` or a nav id added without a route. Deleting it would turn that mistake into
 * a blank pane with no explanation; keeping it names the tab that has no home.
 */

/**
 * What fills the view area while its chunk arrives.
 *
 * A SKELETON RATHER THAN A SPINNER, and it keeps the title: the shell, rail and header
 * are already painted, so the operator is looking at the console — replacing the content
 * with a spinner would say "the app is loading" when only one panel is. The title comes
 * from the nav label they just clicked, so the header does not blank and return.
 *
 * Only ever seen on a cold chunk: once a view has been opened, its chunk is cached and
 * the switch is synchronous.
 */
function ViewLoading({ label }: { label: string }): ReactNode {
  return (
    <Page title={label} desc="Loading…">
      <div className="flex flex-col gap-[10px]">
        <div className="lm-sk h-20 rounded-[var(--radius)]" />
        <div className="lm-sk h-64 rounded-[var(--radius)]" />
      </div>
    </Page>
  );
}

function Stub({ tab }: { tab: string }): ReactNode {
  const copy = { title: NAV_LABEL[tab] ?? tab, desc: 'This tab has no view wired to it.' };
  return (
    <Page title={copy.title} desc={copy.desc}>
      <div className="lm-card flex items-start gap-[10px] p-[18px_20px]">
        <span
          className="flex size-9 shrink-0 items-center justify-center rounded-[9px]"
          style={{ background: 'var(--secondary)' }}
        >
          <Icon name="ic-info" size={17} />
        </span>
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-[14px] font-bold">Not yet implemented</span>
            <StatusBadge verdict="info" label="Planned" />
          </div>
          <p className="text-[13px] leading-[1.55]" style={{ color: 'var(--muted-fg)' }}>
            See <span className="lm-num">Docs/07-delivery/ROADMAP.md</span> for what ships
            when. The compliance engine these modules consume is already live and scoring
            the selected site — see Compliance, Standards and Findings.
          </p>
        </div>
      </div>
    </Page>
  );
}
